/**
 * The document head for the listicles.
 *
 * ── What is marked up, and the one thing that deliberately is not ────────────
 *
 * `ItemList` describes what is on the page: an ordered list of named options.
 * That is a factual description of the document and it is what an assistant
 * reading the markup needs in order to understand the ranking.
 *
 * There is no `Product`, no `Review` and no `aggregateRating`, and the omission
 * is the same one `compare/head.ts` makes for the same reason: those
 * vocabularies assert a RATING of a named thing, and a company rating its
 * competitors in structured data is self-serving review markup — which Google
 * treats as a manual-action-grade violation rather than a style disagreement.
 * The page is allowed to say in prose that we rank ourselves second; it is not
 * allowed to say it in a machine-readable score.
 *
 * ── The `about` link back to the comparison pages ────────────────────────────
 *
 * Finding 4 of the research (docs/SEO-GEO-RESEARCH.md) is that schema markup
 * has close to zero measured effect on whether an assistant cites a page, so
 * nothing here is load-bearing for citations. It is here because it is true and
 * cheap, and because the `ItemList` positions are the one part a summariser can
 * use to reproduce the ranking without re-reading the prose.
 */

import type { ContentLang } from "@fazly/shared/contentLang";

import {
  alternates,
  breadcrumbJsonLd,
  canonical,
  faqJsonLd,
  organizationJsonLd,
  socialMeta,
} from "../seo";
import { BEST_SLUGS, bestIndexPath, bestPath } from "./index";
import type { BestCopy, BestSlug } from "./types";
import type { BestUi } from "./ui";

export function bestHead(
  slug: BestSlug,
  lang: ContentLang,
  copy: BestCopy,
  ui: BestUi,
) {
  const path = bestPath(slug, lang);
  return {
    meta: [
      { title: copy.title },
      { name: "description", content: copy.description },
      ...socialMeta({
        title: copy.title,
        description: copy.description,
        path,
        lang,
        imageAlt: copy.h1,
      }),
      {
        "script:ld+json": {
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: copy.h1,
          description: copy.description,
          url: canonical(path),
          inLanguage: lang,
          numberOfItems: copy.entries.length,
          itemListOrder: "https://schema.org/ItemListOrderDescending",
          itemListElement: copy.entries.map((entry, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: entry.name,
            description: entry.bestFor,
          })),
        },
      },
      { "script:ld+json": faqJsonLd([...copy.faq]) },
      {
        "script:ld+json": breadcrumbJsonLd([
          { name: "Fazly", path: lang === "en" ? "/" : `/${lang}` },
          { name: ui.indexH1, path: bestIndexPath(lang) },
          { name: copy.h1, path },
        ]),
      },
      { "script:ld+json": organizationJsonLd() },
    ],
    links: [
      { rel: "canonical", href: canonical(path) },
      ...alternates((l) => bestPath(slug, l)),
    ],
  };
}

export function bestIndexHead(
  lang: ContentLang,
  ui: BestUi,
  allCopy: Record<BestSlug, BestCopy>,
) {
  const path = bestIndexPath(lang);
  return {
    meta: [
      { title: ui.indexTitle },
      { name: "description", content: ui.indexDescription },
      ...socialMeta({
        title: ui.indexTitle,
        description: ui.indexDescription,
        path,
        lang,
        imageAlt: ui.indexH1,
      }),
      {
        "script:ld+json": {
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: ui.indexH1,
          description: ui.indexDescription,
          url: canonical(path),
          inLanguage: lang,
          mainEntity: {
            "@type": "ItemList",
            numberOfItems: BEST_SLUGS.length,
            itemListElement: BEST_SLUGS.map((slug, i) => ({
              "@type": "ListItem",
              position: i + 1,
              name: allCopy[slug].h1,
              url: canonical(bestPath(slug, lang)),
            })),
          },
        },
      },
      {
        "script:ld+json": breadcrumbJsonLd([
          { name: "Fazly", path: lang === "en" ? "/" : `/${lang}` },
          { name: ui.indexH1, path },
        ]),
      },
    ],
    links: [
      { rel: "canonical", href: canonical(path) },
      ...alternates(bestIndexPath),
    ],
  };
}
